import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Breadcrumb from '../../components/common/Breadcrumb';
import ReactDataTable from 'react-data-table-component';
import {
    IconEdit,
    IconTrash,
    IconBuilding,
    IconBuildingSkyscraper,
    IconBriefcase,
    IconPlus,
    IconLoader2,
    IconShieldLock,
    IconLockAccess,
    IconEye,
    IconBuildingCommunity,
} from '@tabler/icons-react';
import ActionDropdown from '../../components/common/ActionDropdown';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import TenantModal from './TenantModal';
import OrganizationModal from './OrganizationModal';
import ProjectModal from './ProjectModal';
import RoleModal from './RoleModal';
import RolePermissionModal from './RolePermissionModal';
import DepartmentModal from './DepartmentModal';
import UnitModal from './UnitModal';
import OrgUnitLevelModal from './OrgUnitLevelModal';
import RoleDetailsModal from './RoleDetailsModal';
import { useTenants, useCreateTenant, useUpdateTenant, useDeleteTenant } from '../../hooks/useTenants';
import { useOrganizations, useCreateOrganization, useUpdateOrganization, useDeleteOrganization } from '../../hooks/useOrganizations';
import { useProjects, useCreateProject, useUpdateProject, useDeleteProject } from '../../hooks/useProjects';
import { useRoles, useCreateRole, useUpdateRole, useRolePermissionsOverview, useDeleteRole, useAssignPermissionsToRole } from '../../hooks/useRoles';
import { useCreateDepartment, useCreateUnit } from '../../hooks/useDepartments';
import { useOrgUnitLevels, useCreateOrgUnitLevel, useUpdateOrgUnitLevel, useDeleteOrgUnitLevel } from '../../hooks/useOrgUnitLevels';
// import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';

// Tab definitions with required permissions
const tabs = [
    { id: 'tenants', label: 'Tenants', icon: IconBuilding, permissions: ['rbacapp.sys_admin'], superuserOnly: true },
    { id: 'organizations', label: 'Organizations', icon: IconBuildingSkyscraper, permissions: ['rbacapp.sys_admin'], superuserOnly: true },
    { id: 'projects', label: 'Projects', icon: IconBriefcase, permissions: ['rbacapp.sys_admin'] },
    { id: 'organisationUnit', label: 'Organisation Unit', icon: IconLockAccess, permissions: ['rbacapp.rbac_permission_view', 'rbacapp.rbac_admin'] },
    { id: 'roles', label: 'Roles', icon: IconShieldLock, permissions: ['rbacapp.rbac_role_manage', 'rbacapp.rbac_admin'] },
    { id: 'permissions', label: 'Permissions', icon: IconLockAccess, permissions: ['rbacapp.rbac_permission_view', 'rbacapp.rbac_admin'] },
];

export default function SystemSetup() {
    const navigate = useNavigate();
    const { hasAnyPermission } = useAuth();
    const [activeTab, setActiveTab] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [isViewMode, setIsViewMode] = useState(false);
    const [editData, setEditData] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [selectedProjectOrgId, setSelectedProjectOrgId] = useState('');
    const [permissionModalOpen, setPermissionModalOpen] = useState(false);
    const [departmentModalOpen, setDepartmentModalOpen] = useState(false);
    const [unitModalOpen, setUnitModalOpen] = useState(false);
    const [selectedParentOrg, setSelectedParentOrg] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null); // { type, id, name }

    // Tenants
    const { user } = useAuth();
    const isSuperUser = !!user?.is_superuser;

    // Only fetch tenants if superuser
    const { data: tenantsData, isLoading: tenantsLoading, error: tenantsError } = useTenants({}, {
        enabled: isSuperUser || hasAnyPermission(['rbacapp.sys_admin'])
    });
    const createTenantMutation = useCreateTenant();
    const updateTenantMutation = useUpdateTenant();
    const deleteTenantMutation = useDeleteTenant();

    // Organizations
    const { data: orgsData, isLoading: orgsLoading } = useOrganizations({}, {
        enabled: hasAnyPermission(['rbacapp.sys_admin']) || !!user?.is_superuser
    });
    const createOrgMutation = useCreateOrganization();
    const updateOrgMutation = useUpdateOrganization();
    const deleteOrgMutation = useDeleteOrganization();

    // Projects (filtered by organization)
    const { data: projectsData, isLoading: projectsLoading } = useProjects(selectedProjectOrgId);
    const createProjectMutation = useCreateProject();
    const updateProjectMutation = useUpdateProject();
    const deleteProjectMutation = useDeleteProject();
    const deleteRoleMutation = useDeleteRole();

    // Roles (filtered by organization)
    const [selectedRoleOrgId, setSelectedRoleOrgId] = useState('');
    const { data: rolesData, isLoading: rolesLoading } = useRoles(selectedRoleOrgId);
    const createRoleMutation = useCreateRole();
    const updateRoleMutation = useUpdateRole();

    // Departments
    const createDepartmentMutation = useCreateDepartment();

    // Units
    const createUnitMutation = useCreateUnit();

    // Permissions (filtered by organization)
    const [selectedPermOrgId, setSelectedPermOrgId] = useState('');
    const { data: rolePermsData, isLoading: rolePermsLoading } = useRolePermissionsOverview(selectedPermOrgId);
    const [editPermissionRole, setEditPermissionRole] = useState(null); // { id, orgId }
    const clearPermissionsMutation = useAssignPermissionsToRole();

    // Org Unit Levels
    const [selectedOrgUnitLevelOrgId, setSelectedOrgUnitLevelOrgId] = useState('');
    const { data: orgUnitLevelsData, isLoading: orgUnitLevelsLoading } = useOrgUnitLevels({ orgId: selectedOrgUnitLevelOrgId }, { enabled: !!selectedOrgUnitLevelOrgId });
    const createOrgUnitLevelMutation = useCreateOrgUnitLevel();
    const updateOrgUnitLevelMutation = useUpdateOrgUnitLevel();
    const deleteOrgUnitLevelMutation = useDeleteOrgUnitLevel();

    // Extract responses
    const tenants = tenantsData?.results || tenantsData || [];
    const organizations = orgsData?.results || orgsData || [];
    // const locations = locationData?.results || locationData || [];
    const projects = projectsData?.results || projectsData || [];
    const roles = rolesData?.results || rolesData || [];
    const rolePermissions = rolePermsData?.roles || [];
    const orgUnitLevels = orgUnitLevelsData?.results || orgUnitLevelsData || [];

    // Filter tabs based on user permissions
    const allowedTabs = tabs.filter(tab => {
        if (tab.superuserOnly && !isSuperUser) return false;
        return !tab.permissions || hasAnyPermission(tab.permissions);
    });

    // Set initial active tab to first allowed tab
    useEffect(() => {
        if (!activeTab && allowedTabs.length > 0) {
            setActiveTab(allowedTabs[0].id);
        }
    }, [allowedTabs, activeTab]);

    useEffect(() => {
        document.body.classList[modalOpen ? "add" : "remove"]("overflow-hidden");
    }, [modalOpen]);

    const openModal = (type, data = null, viewMode = false) => {
        setModalType(type);
        setEditData(data);
        setIsViewMode(viewMode);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setModalOpen(false);
        setModalType(null);
        setEditData(null);
        setIsViewMode(false);
    };

    const handleSave = async (type, data) => {
        try {
            if (type === 'tenant') {
                if (editData) {
                    const result = await updateTenantMutation.mutateAsync({ id: editData.id, data });
                    const name = result.tenant?.name || result.name || result.tenant_name || data.tenant_name || data.name;
                    toast.success(`Tenant "${name}" updated successfully`);
                } else {
                    const result = await createTenantMutation.mutateAsync(data);
                    const name = result.tenant?.name || result.name || result.tenant_name || data.tenant_name || data.name;
                    toast.success(`Tenant "${name}" created successfully`);
                }
            }
            else if (type === 'organization') {
                if (editData) {
                    const result = await updateOrgMutation.mutateAsync({ id: editData.id, data });
                    const name = result.organization?.name || result.name || data.name;
                    toast.success(`Organization "${name}" updated successfully`);
                } else {
                    const result = await createOrgMutation.mutateAsync(data);
                    const name = result.organization?.name || result.name || data.name;
                    toast.success(`Organization "${name}" created successfully`);
                }
            }

            else if (type === 'project') {
                if (editData) {
                    const result = await updateProjectMutation.mutateAsync({ id: editData.id, data, orgId: selectedProjectOrgId });
                    const name = result.project?.name || result.name || data.name;
                    toast.success(`Project "${name}" updated successfully`);
                } else {
                    const result = await createProjectMutation.mutateAsync(data);
                    const name = result.project?.name || result.name || data.name;
                    toast.success(`Project "${name}" created successfully`);
                }
            }
            else if (type === 'role') {
                // data is { payload, headerOrgId } for roles
                const { payload, headerOrgId } = data;
                if (editData) {
                    const result = await updateRoleMutation.mutateAsync({ id: editData.id, data: payload, orgId: headerOrgId });
                    const name = result.role?.name || result.name || payload.name;
                    toast.success(`Role "${name}" updated successfully`);
                } else {
                    const result = await createRoleMutation.mutateAsync({ data: payload, orgId: headerOrgId });
                    const name = result.role?.name || result.name || payload.name;
                    toast.success(`Role "${name}" created successfully`);
                }
            }
            else if (type === 'orgUnitLevel') {
                if (editData) {
                    const result = await updateOrgUnitLevelMutation.mutateAsync({ id: editData.id, data });
                    const name = result.org_unit_level?.name || result.name || data.name;
                    toast.success(`Unit level "${name}" updated successfully`);
                } else {
                    const result = await createOrgUnitLevelMutation.mutateAsync(data);
                    const name = result.org_unit_level?.name || result.name || data.name;
                    toast.success(`Unit level "${name}" created successfully`);
                }
            }

            closeModal();
        } catch (error) {
            console.error('Error saving:', error);
            // Error is handled by interceptor, but we can add specific context if needed
        }
    };

    const handleDelete = async (type, row) => {
        setItemToDelete({
            type,
            id: row.id,
            name: row.name || row.tenant_name || row.code || 'this item'
        });
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;

        const { type, id } = itemToDelete;

        try {
            if (type === 'tenant') {
                await deleteTenantMutation.mutateAsync(id);
                toast.success(`Tenant "${itemToDelete.name}" deleted successfully`);
            } else if (type === 'organization') {
                await deleteOrgMutation.mutateAsync(id);
                toast.success(`Organization "${itemToDelete.name}" deleted successfully`);
            } else if (type === 'project') {
                await deleteProjectMutation.mutateAsync({ id, orgId: selectedProjectOrgId });
                toast.success(`Project "${itemToDelete.name}" deleted successfully`);
            } else if (type === 'role') {
                await deleteRoleMutation.mutateAsync({ id, orgId: selectedRoleOrgId });
                toast.success(`Role "${itemToDelete.name}" deleted successfully`);
            } else if (type === 'orgUnitLevel') {
                await deleteOrgUnitLevelMutation.mutateAsync(id);
                toast.success(`Unit level "${itemToDelete.name}" deleted successfully`);
            } else if (type === 'permission') {
                await clearPermissionsMutation.mutateAsync({ roleId: id, permissions: [], orgId: selectedPermOrgId });
                toast.success(`All permissions cleared from "${itemToDelete.name}"`);
            }

            setDeleteModalOpen(false);
            setItemToDelete(null);
        } catch (error) {
            console.error('Error deleting:', error);
            // Error is handled by interceptor
        }
    };

    // Action buttons component
    const ActionButtons = ({ type, row }) => (
        <div className='flex items-stretch gap-10'>
            <button
                onClick={() => openModal(type, row)}
                className='bg-sky rounded-[4px] px-10 py-[8px]'
                disabled={isSaving || isDeleting}
            >
                <IconEdit className='text-white w-[18px] h-[18px]' />
            </button>
            <button
                onClick={() => handleDelete(type, row)}
                className='bg-danger rounded-[4px] px-10 py-[8px]'
                disabled={isDeleting || isSaving}
            >
                <IconTrash className='text-white w-[18px] h-[18px]' />
            </button>
        </div>
    );

    // Tenant columns
    const tenantColumns = [
        { name: 'ID', selector: (_, index) => index + 1, sortable: true, width: '60px' },
        { name: 'Tenant Name', selector: row => row.name, sortable: true },
        { name: 'Description', selector: row => row.description, sortable: true },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            cell: row => (
                <span className={`px-10 py-5 rounded-full text-xs font-medium ${row.status === 'active' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                    }`}>
                    {row.status}
                </span>
            )
        },
        {
            name: 'Action', cell: row => (
                <ActionDropdown
                    id={row.id}
                    menuItems={[
                        { label: 'View', icon: IconEye, onClick: () => openModal('tenant', row, true) },
                        { label: 'Edit', icon: IconEdit, onClick: () => openModal('tenant', row) },
                        { label: 'Delete', icon: IconTrash, onClick: () => handleDelete('tenant', row), danger: true },
                    ]}
                />
            )
        },
    ];

    // Organization columns
    const organizationColumns = [
        { name: 'ID', selector: (_, index) => index + 1, sortable: true, width: '60px' },
        { name: 'Tenant Name', selector: row => row.tenant_name, sortable: true },
        { name: 'Organization Name', selector: row => row.name, sortable: true },
        { name: 'Code', selector: row => row.code, sortable: true },
        {
            name: 'Action',
            cell: row => (
                <ActionDropdown
                    id={row.id}
                    menuItems={[
                        { label: 'View', icon: IconEye, onClick: () => openModal('organization', row, true) },
                        { label: 'Edit', icon: IconEdit, onClick: () => openModal('organization', row) },
                        { label: 'Delete', icon: IconTrash, onClick: () => handleDelete('organization', row), danger: true },
                    ]}
                />
            ),
            width: '80px',
        },
    ];

    // Project columns
    const projectColumns = [
        { name: 'ID', selector: (_, index) => index + 1, sortable: true, width: '60px' },
        { name: 'Project Name', selector: row => row.name, sortable: true },
        { name: 'Code', selector: row => row.code, sortable: true },
        { name: 'Donor', selector: row => row.donor, sortable: true },
        { name: 'Description', selector: row => row.description, sortable: true },
        {
            name: 'Status',
            selector: row => row.status,
            cell: row => (
                <span className={`px-10 py-5 rounded-full text-xs font-medium ${row.status === 'active' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                    }`}>
                    {row.status}
                </span>
            )
        },
        {
            name: 'Action',
            cell: row => (
                <ActionDropdown
                    id={row.id}
                    menuItems={[
                        { label: 'View', icon: IconEye, onClick: () => openModal('project', row, true) },
                        { label: 'Edit', icon: IconEdit, onClick: () => openModal('project', row) },
                        { label: 'Delete', icon: IconTrash, onClick: () => handleDelete('project', row), danger: true },
                    ]}
                />
            ),
            width: '80px',
        },
    ];

    // Role columns
    const roleColumns = [
        { name: 'ID', selector: (_, index) => index + 1, sortable: true, width: '60px' },
        { name: 'Role Name', selector: row => row.name, sortable: true },
        { name: 'Code', selector: row => row.code, sortable: true },
        // { name: 'Rank', selector: row => row.rank, sortable: true, width: '80px' },
        {
            name: 'System',
            selector: row => row.is_system,
            cell: row => (
                <span className={`px-10 py-5 rounded-full text-xs font-medium ${row.is_system ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500'}`}>
                    {row.is_system ? 'Yes' : 'No'}
                </span>
            ),
            width: '100px'
        },
        {
            name: 'Action',
            cell: row => (
                <ActionDropdown
                    id={row.id}
                    menuItems={[
                        { label: 'View', icon: IconEye, onClick: () => openModal('role-detail', row) },
                        { label: 'Edit', icon: IconEdit, onClick: () => openModal('role', row) },
                        { label: 'Delete', icon: IconTrash, onClick: () => handleDelete('role', row), danger: true },
                    ]}
                />
            )
        },
    ];

    // Role Permission columns
    const permissionColumns = [
        { name: 'ID', selector: (_, index) => index + 1, sortable: true, width: '60px' },
        { name: 'Role Name', selector: row => row.name, sortable: true },
        { name: 'Code', selector: row => row.code, sortable: true },
        {
            name: 'System',
            selector: row => row.is_system,
            cell: row => (
                <span className={`px-10 py-5 rounded-full text-xs font-medium ${row.is_system ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500'}`}>
                    {row.is_system ? 'Yes' : 'No'}
                </span>
            ),
            width: '100px'
        },
        { name: 'Permissions', selector: row => row.permissions_count, sortable: true, width: '120px' },
        { name: 'Users', selector: row => row.users_count, sortable: true, width: '100px' },
        {
            name: 'Action',
            cell: row => (
                <ActionDropdown
                    id={row.id}
                    menuItems={[
                        {
                            label: 'Edit',
                            icon: IconEdit,
                            onClick: () => {
                                setEditPermissionRole({ id: row.id, orgId: selectedPermOrgId });
                                setPermissionModalOpen(true);
                            }
                        },
                        {
                            label: 'Delete',
                            icon: IconTrash,
                            danger: true,
                            onClick: () => handleDelete('permission', row),
                        },
                    ]}
                />
            ),
        },
    ];

    // Org Unit Level columns
    const orgUnitLevelColumns = [
        { name: 'Rank', selector: row => row.rank, sortable: true, width: '80px', sortFunction: (a, b) => b.rank - a.rank },
        { name: 'Name', selector: row => row.name, sortable: true },
        { name: 'Code', selector: row => row.code, sortable: true },
        { name: 'Description', selector: row => row.description, sortable: true, grow: 2 },
        {
            name: 'Status',
            selector: row => row.is_active,
            cell: row => (
                <span className={`px-10 py-5 rounded-full text-xs font-medium ${row.is_active ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                    {row.is_active ? 'Active' : 'Inactive'}
                </span>
            ),
            width: '100px'
        },
        {
            name: 'Action',
            cell: row => (
                <ActionDropdown
                    id={row.id}
                    menuItems={[
                        { label: 'View', icon: IconEye, onClick: () => openModal('orgUnitLevel', row, true) },
                        { label: 'Edit', icon: IconEdit, onClick: () => openModal('orgUnitLevel', row) },
                        { label: 'Delete', icon: IconTrash, onClick: () => handleDelete('orgUnitLevel', row), danger: true },
                    ]}
                />
            )
        },
    ];

    const getActiveData = () => {
        switch (activeTab) {
            case 'tenants': return tenants;
            case 'organizations': return organizations;
            // case 'locations': return locations;
            case 'projects': return projects;
            case 'roles': return roles;
            case 'permissions': return rolePermissions;
            case 'organisationUnit': return orgUnitLevels;
            default: return [];
        }
    };

    const getActiveColumns = () => {
        switch (activeTab) {
            case 'tenants': return tenantColumns;
            case 'organizations': return organizationColumns;
            // case 'locations': return locationColumns;
            case 'projects': return projectColumns;
            case 'roles': return roleColumns;
            case 'permissions': return permissionColumns;
            case 'organisationUnit': return orgUnitLevelColumns;
            default: return [];
        }
    };

    const isLoading = (activeTab === 'tenants' && tenantsLoading) ||
        (activeTab === 'projects' && projectsLoading && selectedProjectOrgId) ||
        (activeTab === 'roles' && rolesLoading && selectedRoleOrgId) ||
        (activeTab === 'permissions' && rolePermsLoading && selectedPermOrgId) ||
        (activeTab === 'organisationUnit' && orgUnitLevelsLoading && selectedOrgUnitLevelOrgId);
    const error = activeTab === 'tenants' && tenantsError;

    const filteredData = getActiveData().filter(item => {
        if (!item) return false;
        const searchStr = filterText.toLowerCase();
        return Object.values(item).some(val =>
            String(val).toLowerCase().includes(searchStr)
        );
    });

    const handleClear = () => {
        if (filterText) {
            setResetPaginationToggle(!resetPaginationToggle);
            setFilterText('');
        }
    };

    const getModalTitle = () => {
        const action = editData ? 'Edit' : 'Add';
        switch (modalType) {
            case 'tenant': return isViewMode ? 'Tenant Details' : `${action} Tenant`;
            case 'organization': return isViewMode ? 'Organization Details' : `${action} Organization`;
            case 'location': return `${action} Location`;
            case 'project': return isViewMode ? 'Project Details' : `${action} Project`;
            case 'role': return `${action} Role`;
            case 'orgUnitLevel': return `${action} Organization Unit Level`;
            default: return '';
        }
    };

    const isSaving = createTenantMutation.isPending || updateTenantMutation.isPending ||
        createOrgMutation.isPending || updateOrgMutation.isPending ||
        createProjectMutation.isPending || updateProjectMutation.isPending ||
        createRoleMutation.isPending || updateRoleMutation.isPending ||
        createOrgUnitLevelMutation.isPending || updateOrgUnitLevelMutation.isPending;

    const isDeleting = deleteTenantMutation.isPending || deleteOrgMutation.isPending ||
        deleteProjectMutation.isPending || deleteRoleMutation.isPending ||
        deleteOrgUnitLevelMutation.isPending || clearPermissionsMutation.isPending;

    return (
        <>
            <Breadcrumb title="System Setup" path="Administration / System Setup" />

            <div className='bg-bg-dark rounded-lg shadow-light md:p-20 p-15'>
                {/* Tabs - filtered by permissions */}
                <div className='flex flex-wrap gap-10 mb-20 border-b border-border pb-15'>
                    {allowedTabs.map(tab => {
                        const TabIcon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => {
                                    setActiveTab(tab.id);
                                    setFilterText('');
                                }}
                                className={`flex items-center gap-8 px-15 py-10 rounded-lg font-medium transition-all ${activeTab === tab.id
                                    ? 'bg-primary text-white'
                                    : 'bg-bg-light text-text-secondary hover:bg-primary/10'
                                    }`}
                            >
                                <TabIcon size={18} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Header */}
                <div className='flex flex-wrap items-center justify-between gap-15 mb-20'>
                    <p className='text-[16px]/[19.2px] font-medium capitalize'>
                        {activeTab} List
                    </p>
                    <div className='flex items-center gap-10'>
                        {/* Organization filter for projects tab */}
                        {activeTab === 'projects' && (
                            <select
                                value={selectedProjectOrgId}
                                onChange={(e) => setSelectedProjectOrgId(e.target.value)}
                                className='bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none'
                            >
                                <option value=''>Select Organization</option>
                                {organizations.map(org => (
                                    <option key={org.id} value={org.id}>
                                        {org.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        {/* Organization filter for roles tab */}
                        {activeTab === 'roles' && (
                            <select
                                value={selectedRoleOrgId}
                                onChange={(e) => setSelectedRoleOrgId(e.target.value)}
                                className='bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none'
                            >
                                <option value=''>Select Organization</option>
                                {organizations.map(org => (
                                    <option key={org.id} value={org.id}>
                                        {org.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        {/* Organization filter for permissions tab */}
                        {activeTab === 'permissions' && (
                            <select
                                value={selectedPermOrgId}
                                onChange={(e) => setSelectedPermOrgId(e.target.value)}
                                className='bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none'
                            >
                                <option value=''>Select Organization</option>
                                {organizations.map(org => (
                                    <option key={org.id} value={org.id}>
                                        {org.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        {/* Organization filter for orgUnitLevel tab */}
                        {activeTab === 'organisationUnit' && (
                            <select
                                value={selectedOrgUnitLevelOrgId}
                                onChange={(e) => setSelectedOrgUnitLevelOrgId(e.target.value)}
                                className='bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none'
                            >
                                <option value=''>Select Organization</option>
                                {organizations.map(org => (
                                    <option key={org.id} value={org.id}>
                                        {org.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        {activeTab === 'organizations' && (
                            <button
                                onClick={() => navigate('/organization-structure')}
                                className='btn btn-outline-secondary flex items-center gap-5 mr-3'
                            >
                                <IconBuildingCommunity size={18} />
                                Manage Department and Unit
                            </button>
                        )}
                        <button
                            onClick={() => {
                                if (activeTab === 'permissions') {
                                    setPermissionModalOpen(true);
                                } else if (activeTab === 'organisationUnit') {
                                    openModal('orgUnitLevel');
                                } else {
                                    openModal(activeTab.slice(0, -1));
                                }
                            }}
                            className='btn btn-primary flex items-center gap-5'
                        >
                            <IconPlus size={18} />
                            {activeTab === 'permissions' ? 'Assign to Role' : 'Add New'}
                        </button>
                    </div>
                </div>

                {/* Search */}
                <div className='flex gap-5 mb-10 w-full justify-end'>
                    <div className='relative w-full max-w-[221px]'>
                        <input
                            type='text'
                            value={filterText}
                            onChange={e => setFilterText(e.target.value)}
                            placeholder='Search here...'
                            className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none'
                        />
                    </div>
                    <button onClick={handleClear} className="btn btn-danger">
                        Clear
                    </button>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className='flex items-center justify-center py-20'>
                        <IconLoader2 className='w-8 h-8 animate-spin text-primary' />
                        <span className='ml-2'>Loading...</span>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className='bg-danger/10 text-danger p-4 rounded-lg mb-4'>
                        Error loading data: {error.message}
                    </div>
                )}

                {/* No org selected message for projects */}
                {activeTab === 'projects' && !selectedProjectOrgId && (
                    <div className='text-center py-10 text-text-secondary'>
                        Please select an organization to view projects
                    </div>
                )}

                {/* No org selected message for roles */}
                {activeTab === 'roles' && !selectedRoleOrgId && (
                    <div className='text-center py-10 text-text-secondary'>
                        Please select an organization to view roles
                    </div>
                )}

                {/* No org selected message for permissions */}
                {activeTab === 'permissions' && !selectedPermOrgId && (
                    <div className='text-center py-10 text-text-secondary'>
                        Please select an organization to view role permissions
                    </div>
                )}

                {/* No org selected message for orgUnitLevel */}
                {activeTab === 'organisationUnit' && !selectedOrgUnitLevelOrgId && (
                    <div className='text-center py-10 text-text-secondary'>
                        Please select an organization to view unit levels
                    </div>
                )}

                {/* Data Table */}
                {!isLoading && !error &&
                    (activeTab !== 'projects' || selectedProjectOrgId) &&
                    (activeTab !== 'roles' || selectedRoleOrgId) &&
                    (activeTab !== 'permissions' || selectedPermOrgId) &&
                    (activeTab !== 'organisationUnit' || selectedOrgUnitLevelOrgId) && (
                        <div className='react-data-table'>
                            <ReactDataTable
                                columns={getActiveColumns()}
                                data={filteredData}
                                pagination
                                paginationResetDefaultPage={resetPaginationToggle}
                                noDataComponent={<div className='py-10 text-text-secondary'>No data available</div>}
                            />
                        </div>
                    )}
            </div >

            {/* Modals */}
            {modalOpen && modalType === 'tenant' && (
                <TenantModal
                    isOpen={modalOpen}
                    onClose={closeModal}
                    onSave={(data) => handleSave('tenant', data)}
                    editData={editData}
                    title={getModalTitle()}
                    isLoading={isSaving}
                    isViewMode={isViewMode}
                />
            )}


            {modalOpen && modalType === 'organization' && (
                <OrganizationModal
                    isOpen={modalOpen}
                    onClose={closeModal}
                    onSave={(data) => handleSave('organization', data)}
                    editData={editData}
                    title={getModalTitle()}
                    tenants={tenants}
                    isViewMode={isViewMode}
                />
            )}

            {
                modalOpen && modalType === 'project' && (
                    <ProjectModal
                        isOpen={modalOpen}
                        onClose={closeModal}
                        onSave={(data) => handleSave('project', data)}
                        editData={editData}
                        title={getModalTitle()}
                        organizations={organizations}
                        selectedOrgId={selectedProjectOrgId}
                        isViewMode={isViewMode}
                    />
                )
            }

            {modalOpen && modalType === 'role' && (
                <RoleModal
                    isOpen={modalOpen}
                    onClose={closeModal}
                    onSave={(payload, headerOrgId) => handleSave('role', { payload, headerOrgId })}
                    editData={editData}
                    title={getModalTitle()}
                    isLoading={isSaving}
                />
            )}

            {modalOpen && modalType === 'role-detail' && (
                <RoleDetailsModal
                    isOpen={modalOpen}
                    onClose={closeModal}
                    role={editData}
                    title="Role Details"
                />
            )}

            {
                modalOpen && modalType === 'orgUnitLevel' && (
                    <OrgUnitLevelModal
                        isOpen={modalOpen}
                        onClose={closeModal}
                        onSave={(data) => handleSave('orgUnitLevel', data)}
                        editData={editData}
                        title={getModalTitle()}
                        isLoading={isSaving}
                        isViewMode={isViewMode}
                    />
                )
            }

            {/* Permission Assignment Modal */}
            <RolePermissionModal
                isOpen={permissionModalOpen}
                onClose={() => { setPermissionModalOpen(false); setEditPermissionRole(null); }}
                organizations={organizations}
                initialOrgId={editPermissionRole?.orgId || ''}
                initialRoleId={editPermissionRole?.id || ''}
            />

            {/* Department Modal */}
            <DepartmentModal
                isOpen={departmentModalOpen}
                onClose={() => {
                    setDepartmentModalOpen(false);
                    setSelectedParentOrg(null);
                    setSelectedProject(null);
                }}
                onSave={async (data) => {
                    try {
                        const result = await createDepartmentMutation.mutateAsync(data);
                        const name = result.department?.name || result.name || data.name;
                        toast.success(`Department "${name}" created successfully`);
                        setDepartmentModalOpen(false);
                        setSelectedParentOrg(null);
                        setSelectedProject(null);
                    } catch (error) {
                        console.error('Error creating department:', error);
                    }
                }}
                organizations={organizations}
                projects={projects}
                editData={
                    selectedProject
                        ? { org: selectedProject.organization, project: selectedProject.id }
                        : selectedParentOrg
                            ? { org: selectedParentOrg.id }
                            : null
                }
                title="Create Department"
                isLoading={createDepartmentMutation.isPending}
            />

            {/* Unit Modal */}
            <UnitModal
                isOpen={unitModalOpen}
                onClose={() => {
                    setUnitModalOpen(false);
                    setSelectedParentOrg(null);
                    setSelectedProject(null);
                }}
                onSave={async (data) => {
                    try {
                        const result = await createUnitMutation.mutateAsync(data);
                        const name = result.unit?.name || result.name || data.name;
                        toast.success(`Unit "${name}" created successfully`);
                        setUnitModalOpen(false);
                        setSelectedParentOrg(null);
                        setSelectedProject(null);
                    } catch (error) {
                        console.error('Error creating unit:', error);
                    }
                }}
                selectedOrgId={selectedProject?.organization || selectedProject?.org || selectedParentOrg?.id}
                title="Create Unit"
                isLoading={createUnitMutation.isPending}
            />

            <ConfirmDeleteModal
                isOpen={deleteModalOpen}
                onClose={() => { setDeleteModalOpen(false); setItemToDelete(null); }}
                onConfirm={confirmDelete}
                title={`Delete ${itemToDelete?.type?.charAt(0).toUpperCase() + itemToDelete?.type?.slice(1) || 'Item'}`}
                message={`Are you sure you want to delete this ${itemToDelete?.type || 'item'}? This action will remove all associated data and cannot be undone.`}
                itemName={itemToDelete?.name}
                isLoading={isDeleting}
            />
        </>
    );
}
